import { useLayoutEffect, useEffect, useState, VFC } from "react";
import { useNavigate } from "react-router-dom";
import { Grid, Box, Button, Card, Typography, AccordionSummary, AccordionDetails } from "@mui/material";
import ButtonArea from "../common/ButtonArea";
import { BackButton } from "../common/BackButton";
import {
  TenantDetailRequest,
  TenantDetailResponse,
} from "./api/TenantForm";
import { TenantService } from "./api/TenantService";
import { AxiosResponse } from "axios";
import Constants from "../../utils/Constants";
import { ResultDialog, ResultResponse } from "../common/dialog/ResultDialog";
import TenantDetailView from "../common/detailview/TenantDetailView";
import { ApplicationProtectedRouting } from "../../Routing";
import TenantEditMode from "../../entity/TenantEditMode";
import { ContractSearchRequest, ContractSearchResponse } from "../contract/api/ContractForm";
import { ContractService } from "../contract/api/ContractService";
import ContractList from "../contract/ContractList";
import MdmUserDialog from "../mdmuser/MdmUserEditDialog";
import MdmUserDialogMode from "../../entity/MdmUserDialogMode";
import { IS_MFAAUTH } from "../../entity/MdmUser";
import { IS_MAEBARAI, TENANT_STATUS, UPPER_OPERATION } from "../../entity/Tenant";

import { Loading } from "../common/Loading";
import { ProtectedApiAuth } from "../protected/ProtectedApiAuth";

const TenantDetail: VFC = () => {
  const PAGE_NAME = "テナント詳細";
  const navigate = useNavigate();

  /***************************************************************
   * State管理
   **************************************************************/
  // 共通ローディング
  const [isLoading, setIsLoading] = useState(false);

  // テナントリクエスト
  const [request, setRequest] = useState<TenantDetailRequest>(
    new TenantDetailRequest()
  );

  // テナント情報
  const [tenant, setTenant] = useState<TenantDetailResponse>(
    new TenantDetailResponse()
  );

  // テナントの契約情報
  const [contractList, setContractList] = useState<ContractSearchResponse[]>(
    [] as ContractSearchResponse[]
  );

  // 編集ボタン(活性・非活性)
  const [isDisable, setIsDisable] = useState(false);

  // ユーザー操作ダイヤログ
  const [isMdmUserOpen, setIsMdmUserOpen] = useState(false);

  // 共通ダイアログ(ステータス)
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // 処理結果
  const [resultResponse, setResultResponse] = useState<ResultResponse>({
    status: "",
    code: "",
    displayMessage: "",
    result: [],
    errorMessages: [],
  });

  /***************************************************************
   * 共通処理
   **************************************************************/
  // 処理結果ダイアログ(CLOSE)
  const handleResultDialogClose = () => {
    setIsDialogOpen(false);
  };
  // 処理結果を設定
  const setStateResultResponse = (
    status: string,
    code: string,
    displayMessage: string,
    result: string
  ) => {
    // TODO setterを利用するよう変更
    resultResponse.status = status;
    resultResponse.code = code;
    resultResponse.displayMessage = displayMessage;
    resultResponse.result = [result];

    setIsDialogOpen(true);
    setIsDisable(true);
    setResultResponse(resultResponse);
  };

  /***************************************************************
   * 初期表示処理
   **************************************************************/
  useEffect(() => {
    // テナントIDを設定
    request.id = sessionStorage.getItem("TENANT_ID") ?? "";
    setRequest(request);
    // テナント情報取得
    findByTenantId();
    findTenantIdContract();
  }, []);

  /***************************************************************
   * レイアウト
   **************************************************************/
  // テナント詳細取得API
  const findByTenantId = () => {
    const service = new TenantService();
    if ("" !== request.id) {
      setIsLoading(true);
      service
        .findByTenantId(request.id)
        .then((response: AxiosResponse) => {
          if (Constants.ERR_STATUS_OK === response.data.status) {
            const result = response.data.result as TenantDetailResponse;
            result.id = request.id;
            result.isMfaauth = IS_MFAAUTH[result.isMfaauth];
            result.isMaebarai = IS_MAEBARAI[result.isMaebarai];
            result.tenantStatus = TENANT_STATUS[result.tenantStatus];

            if (!Number.isNaN(+result.upperOperation)) {
              result.upperOperation = UPPER_OPERATION[+result.upperOperation];
            }
            
            setTenant(response.data.result);
          } else {
            setIsDialogOpen(true);
            setIsDisable(true);
            setResultResponse(response.data);
          }
        })
        .catch((err) => {
          setStateResultResponse(
            Constants.ERR_STATUS_NG,
            err.response.status,
            err.response.statusText,
            Constants.E_MSG_SYSTEM_ERR
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setStateResultResponse(
        Constants.ERR_STATUS_NG,
        Constants.ERR_CODE_404,
        Constants.T_MSG_NO_DATA,
        Constants.W_MSG_NO_DATA
      );
    }
  };

  // テナント内の契約を取得
  const findTenantIdContract = () => {
    const service = new ContractService();
    if ("" !== request.id) {
      const req = new ContractSearchRequest();
      req.tenantId = request.id;
      service
        .findByConditions(req)
        .then((response: AxiosResponse) => {
          if (Constants.ERR_STATUS_OK === response.data.status) {
            setContractList(response.data.data);
          } else {
            setIsDialogOpen(true);
            setIsDisable(true);
            setResultResponse(response.data);
          }
        })
        .catch((err) => {
          setStateResultResponse(
            Constants.ERR_STATUS_NG,
            err.response.status,
            err.response.statusText,
            Constants.E_MSG_SYSTEM_ERR
          );
        })
        .finally(() => {
          // TODO 必要ならローディング
        });
    } else {
    }
  };

  /***************************************************************
   * 編集
   **************************************************************/
  // 編集ボタン押下
  const editButtonClick = (id: string) => {
    // 利用者編集/新規追加画面へ遷移する
    sessionStorage.setItem("TENANT_ID", id);
    navigate(ApplicationProtectedRouting.tenantEdit.path(id), {state: {
      mode: TenantEditMode.EDIT,
    }});
  };

  /***************************************************************
   * ユーザーの追加
   **************************************************************/
  // ユーザーダイヤログ（OPEN）
  const createUserButtonClick = (id: string) => {
    // 画面遷移なしでポップアップ表示
    setIsMdmUserOpen(true);
  }

  // ユーザーダイヤログ（CLOSE）
  const handleMdmUserClose = () => {
    setIsMdmUserOpen(false);
  }

  /***************************************************************
   * 権限処理
   **************************************************************/
  // 権限なしのオーバーライド
  const overrideFunction = (response: ResultResponse) => {
    setResultResponse(response);
    setIsDialogOpen(true);
  }

  /***************************************************************
   * レイアウト
   **************************************************************/

  return (
    <>
      <Box>
        {/* ボタンの配置 */}
        <ButtonArea pageName={PAGE_NAME}>
          <BackButton />
          {/* ボタン1つ分の空白 */}
          <Button
            disabled={true}
          ></Button>
          <ProtectedApiAuth
            authcd={Constants.AUTH_MDM_USER_CREATE}
            defaultFunction={() => createUserButtonClick(request.id)}
            overrideFunction={overrideFunction}
          >
            <Button
              variant="contained"
              color="primary"
              sx={{ textTransform: "none" }}
              disabled={isDisable}
            >
              LiNKアカウント追加
            </Button>
          </ProtectedApiAuth>
          <Button
            variant="contained"
            color="primary"
            disabled={isDisable}
            onClick={() => editButtonClick(request.id)}
          >
            編集
          </Button>
        </ButtonArea>
        <Box padding={1}>
          <Card>
            {/* テナント情報 */}
            <Box 
            sx={{ p: 3 }}
            //  flex={"1"} 
            //  style={{ overflowY: "scroll" }}
            >
              <Grid container direction="column" spacing={2}>
                <TenantDetailView tenant={tenant} />
              </Grid>
            </Box>
          </Card>
        </Box>
        <Box padding={1}>
        <Card>
        { contractList !== null && contractList.length !== 0 ? <>
            <Box>
              <ContractList isSearch={false} preContractList={contractList} />
            </Box>
          </> : <>
            <Box>
              <Grid container alignItems={'center'} justifyContent={'center'}>
                {/* 契約なし */}
                <Grid item xs={12} padding={3}>
                  <Typography variant="h2" align="center" fontSize={"1.25rem"} noWrap>
                    契約情報なし
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </>
        }
        </Card>
        </Box>
      </Box>

      {/* MDMユーザーダイアログ */}
      <MdmUserDialog
        isAlertDialogOpen={isMdmUserOpen}
        mode="保存"
        mdmUserId=""
        defaultDialogMode={MdmUserDialogMode.CREATE}
        handleAlertCancel={handleMdmUserClose}
        handleAlertOk={() => {}} 
      />

      {/* 処理結果ダイアログ */}
      <ResultDialog
        isDialogOpen={isDialogOpen}
        resultResponse={resultResponse}
        handler={handleResultDialogClose}
      />
      
      {/* 処理ローディング */}
      <Loading isLoading={isLoading} />
    </>
  );
};

export default TenantDetail;
